/**
 * Countdown Service
 *
 * Offline-first service for managing countdown timers.
 * All writes persist to IndexedDB immediately, then enqueue a sync operation
 * for server propagation when connectivity is available.
 *
 * Key behaviors:
 * - Max 20 active (non-expired) countdowns per user
 * - Name validation: 1-100 characters
 * - Target date validation: valid YYYY-MM-DD format
 * - Remaining time calculation from target date/time to current time
 * - Expired countdown detection and marking
 * - Automatic cleanup of countdowns expired > 30 days ago
 * - Reordering support via sortOrder field
 *
 * @module countdownService
 * @see Requirements 9.1, 9.2, 9.3, 9.4, 9.7, 12.4
 */

import { v4 as uuidv4 } from 'uuid';
import type { CountdownTimer, SyncQueueItem } from '../types';
import { SyncStatus } from '../types/enums';
import { db } from './localDb';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Maximum number of active (non-expired) countdowns allowed. */
const MAX_ACTIVE_COUNTDOWNS = 20;

/** Maximum name length in characters. */
const MAX_NAME_LENGTH = 100;

/** Minimum name length in characters. */
const MIN_NAME_LENGTH = 1;

/** Number of days after expiry before automatic cleanup. */
const EXPIRED_RETENTION_DAYS = 30;

/**
 * Enqueue a sync item for the countdown module.
 */
async function enqueueSyncChange(
  entityId: string,
  action: 'create' | 'update' | 'delete',
  payload: Record<string, unknown>
): Promise<void> {
  const size = await db.syncQueue.count();
  if (size >= 1000) {
    return;
  }
  await db.syncQueue.add({
    module: 'countdown',
    entityId,
    action,
    timestamp: new Date().toISOString(),
    payload,
    retryCount: 0,
  } as unknown as SyncQueueItem);
}

/** Regex for validating YYYY-MM-DD date format. */
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Regex for validating HH:mm time format (24-hour). */
const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

// ─── Types ───────────────────────────────────────────────────────────────────

/** Input for creating a new countdown. */
export interface CreateCountdownInput {
  name: string;
  targetDate: string;
  targetTime?: string;
  color?: string;
  emoji?: string;
}

/** Input for updating an existing countdown. */
export type UpdateCountdownInput = Partial<
  Pick<CountdownTimer, 'name' | 'targetDate' | 'targetTime' | 'color' | 'emoji' | 'sortOrder'>
>;

/** Remaining time breakdown returned by getRemainingTime. */
export interface RemainingTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

// ─── Validation Helpers ──────────────────────────────────────────────────────

/**
 * Validate countdown name: 1-100 characters.
 * @throws Error if name is invalid.
 */
function validateName(name: string): void {
  if (!name || name.trim().length < MIN_NAME_LENGTH) {
    throw new Error(
      `Countdown name must be at least ${MIN_NAME_LENGTH} character(s).`
    );
  }
  if (name.length > MAX_NAME_LENGTH) {
    throw new Error(
      `Countdown name must not exceed ${MAX_NAME_LENGTH} characters.`
    );
  }
}

/**
 * Validate target date is a valid YYYY-MM-DD string that represents a real date.
 * @throws Error if date is invalid.
 */
function validateTargetDate(targetDate: string): void {
  if (!DATE_REGEX.test(targetDate)) {
    throw new Error('Target date must be in YYYY-MM-DD format.');
  }

  const parsed = new Date(targetDate + 'T00:00:00');
  if (isNaN(parsed.getTime())) {
    throw new Error('Target date is not a valid date.');
  }

  // Verify the date components match (e.g., reject 2024-02-30)
  const [year, month, day] = targetDate.split('-').map(Number);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() + 1 !== month ||
    parsed.getDate() !== day
  ) {
    throw new Error('Target date is not a valid date.');
  }
}

/**
 * Validate optional target time is in HH:mm 24-hour format.
 * @throws Error if time format is invalid.
 */
function validateTargetTime(targetTime: string | undefined | null): void {
  if (targetTime != null && targetTime !== '' && !TIME_REGEX.test(targetTime)) {
    throw new Error('Target time must be in HH:mm 24-hour format.');
  }
}

// ─── Service Functions ───────────────────────────────────────────────────────

/**
 * Create a new countdown timer.
 *
 * Validates input, enforces the max 20 active countdowns limit,
 * persists to IndexedDB, and enqueues a sync operation.
 *
 * @param input - Countdown creation parameters.
 * @param userId - The current user's Cognito sub ID.
 * @returns The created CountdownTimer.
 * @throws Error if validation fails or the 20-countdown limit is reached.
 */
export async function createCountdown(
  input: CreateCountdownInput,
  userId: string
): Promise<CountdownTimer> {
  // Validate input
  validateName(input.name);
  validateTargetDate(input.targetDate);
  validateTargetTime(input.targetTime);

  // Enforce max 20 active countdowns
  const activeCountdowns = await db.countdowns
    .where('userId')
    .equals(userId)
    .and((c) => !c.isExpired)
    .toArray();
  const activeCount = activeCountdowns.length;

  if (activeCount >= MAX_ACTIVE_COUNTDOWNS) {
    throw new Error(
      `Maximum of ${MAX_ACTIVE_COUNTDOWNS} active countdowns reached. Delete or let existing countdowns expire before creating new ones.`
    );
  }

  // Determine sort order (append at end)
  const existingCountdowns = await db.countdowns
    .where('userId')
    .equals(userId)
    .toArray();
  const maxSortOrder = existingCountdowns.reduce(
    (max, c) => Math.max(max, c.sortOrder),
    -1
  );

  const now = new Date().toISOString();
  const countdown: CountdownTimer = {
    countdownId: uuidv4(),
    userId,
    name: input.name.trim(),
    targetDate: input.targetDate,
    targetTime: input.targetTime ?? null,
    color: input.color ?? null,
    emoji: input.emoji ?? null,
    sortOrder: maxSortOrder + 1,
    isExpired: false,
    expiredAt: null,
    createdAt: now,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist to IndexedDB
  await db.countdowns.put(countdown);

  // Enqueue sync
  await enqueueSyncChange(countdown.countdownId, 'create', countdown as unknown as Record<string, unknown>);

  return countdown;
}

/**
 * Get all countdowns for a user (both active and expired).
 *
 * @param userId - The current user's Cognito sub ID.
 * @returns Array of CountdownTimer objects sorted by sortOrder.
 */
export async function getCountdowns(userId: string): Promise<CountdownTimer[]> {
  return db.countdowns
    .where('userId')
    .equals(userId)
    .sortBy('sortOrder');
}

/**
 * Get only active (non-expired) countdowns for a user.
 *
 * @param userId - The current user's Cognito sub ID.
 * @returns Array of active CountdownTimer objects sorted by target date (nearest first).
 */
export async function getActiveCountdowns(userId: string): Promise<CountdownTimer[]> {
  const countdowns = await db.countdowns
    .where('userId')
    .equals(userId)
    .and((c) => !c.isExpired)
    .toArray();

  // Default sort: nearest target date first
  return countdowns.sort((a, b) => {
    const dateA = a.targetDate + (a.targetTime ?? '23:59');
    const dateB = b.targetDate + (b.targetTime ?? '23:59');
    return dateA.localeCompare(dateB);
  });
}

/**
 * Get expired countdowns for a user (for the "Past" section).
 *
 * @param userId - The current user's Cognito sub ID.
 * @returns Array of expired CountdownTimer objects sorted by expiredAt descending.
 */
export async function getExpiredCountdowns(userId: string): Promise<CountdownTimer[]> {
  const countdowns = await db.countdowns
    .where('userId')
    .equals(userId)
    .and((c) => c.isExpired)
    .toArray();

  // Sort by most recently expired first
  return countdowns.sort((a, b) => {
    const expA = a.expiredAt ?? '';
    const expB = b.expiredAt ?? '';
    return expB.localeCompare(expA);
  });
}

/**
 * Update an existing countdown timer.
 *
 * Validates any updated fields, persists to IndexedDB, and enqueues sync.
 *
 * @param countdownId - The ID of the countdown to update.
 * @param updates - Partial countdown fields to update.
 * @param userId - The current user's Cognito sub ID.
 * @returns The updated CountdownTimer.
 * @throws Error if the countdown is not found or validation fails.
 */
export async function updateCountdown(
  countdownId: string,
  updates: UpdateCountdownInput,
  userId: string
): Promise<CountdownTimer> {
  const existing = await db.countdowns.get(countdownId);

  if (!existing || existing.userId !== userId) {
    throw new Error('Countdown not found.');
  }

  // Validate updated fields
  if (updates.name !== undefined) {
    validateName(updates.name);
  }
  if (updates.targetDate !== undefined) {
    validateTargetDate(updates.targetDate);
  }
  if (updates.targetTime !== undefined) {
    validateTargetTime(updates.targetTime);
  }

  const now = new Date().toISOString();
  const updated: CountdownTimer = {
    ...existing,
    ...updates,
    name: updates.name !== undefined ? updates.name.trim() : existing.name,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist to IndexedDB
  await db.countdowns.put(updated);

  // Enqueue sync
  await enqueueSyncChange(countdownId, 'update', updated as unknown as Record<string, unknown>);

  return updated;
}

/**
 * Delete a countdown timer.
 *
 * Removes from IndexedDB immediately and enqueues a sync delete operation.
 *
 * @param countdownId - The ID of the countdown to delete.
 * @param userId - The current user's Cognito sub ID.
 * @throws Error if the countdown is not found.
 */
export async function deleteCountdown(
  countdownId: string,
  userId: string
): Promise<void> {
  const existing = await db.countdowns.get(countdownId);

  if (!existing || existing.userId !== userId) {
    throw new Error('Countdown not found.');
  }

  // Remove from IndexedDB
  await db.countdowns.delete(countdownId);

  // Enqueue sync
  await enqueueSyncChange(countdownId, 'delete', { countdownId, userId });
}

/**
 * Calculate the remaining time for a countdown timer.
 *
 * Returns the time difference broken down into days, hours, minutes, seconds.
 * If the target has passed, isExpired is true and all values are 0.
 *
 * @param countdown - The countdown timer to calculate remaining time for.
 * @param now - Optional current timestamp for testing (defaults to Date.now()).
 * @returns The remaining time breakdown.
 */
export function getRemainingTime(
  countdown: CountdownTimer,
  now?: number
): RemainingTime {
  const currentTime = now ?? Date.now();

  // Build target timestamp from date + optional time
  const timeStr = countdown.targetTime ?? '00:00';
  const targetTimestamp = new Date(
    `${countdown.targetDate}T${timeStr}:00`
  ).getTime();

  const diffMs = targetTimestamp - currentTime;

  if (diffMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds, isExpired: false };
}

/**
 * Check all countdowns and mark any that have reached their target as expired.
 *
 * Queries all non-expired countdowns, checks if their target date/time has passed,
 * and marks them as expired with an expiredAt timestamp.
 *
 * @param userId - The current user's Cognito sub ID.
 */
export async function checkExpiredCountdowns(userId: string): Promise<void> {
  const activeCountdowns = await db.countdowns
    .where('userId')
    .equals(userId)
    .and((c) => !c.isExpired)
    .toArray();

  const now = Date.now();
  const nowIso = new Date().toISOString();

  for (const countdown of activeCountdowns) {
    const remaining = getRemainingTime(countdown, now);

    if (remaining.isExpired) {
      const updated: CountdownTimer = {
        ...countdown,
        isExpired: true,
        expiredAt: nowIso,
        updatedAt: nowIso,
        syncStatus: SyncStatus.Pending,
      };

      await db.countdowns.put(updated);

      await enqueueSyncChange(countdown.countdownId, 'update', updated as unknown as Record<string, unknown>);
    }
  }
}

/**
 * Clean up expired countdowns that have been expired for more than 30 days.
 *
 * Removes them from IndexedDB and enqueues sync delete operations.
 *
 * @param userId - The current user's Cognito sub ID.
 * @returns The number of countdowns removed.
 */
export async function cleanupExpired(userId: string): Promise<number> {
  const expiredCountdowns = await db.countdowns
    .where('userId')
    .equals(userId)
    .and((c) => c.isExpired)
    .toArray();

  const now = Date.now();
  const cutoffMs = EXPIRED_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  let removedCount = 0;

  for (const countdown of expiredCountdowns) {
    if (!countdown.expiredAt) {
      continue;
    }

    const expiredTime = new Date(countdown.expiredAt).getTime();
    const elapsed = now - expiredTime;

    if (elapsed > cutoffMs) {
      await db.countdowns.delete(countdown.countdownId);

      await enqueueSyncChange(countdown.countdownId, 'delete', { countdownId: countdown.countdownId, userId });

      removedCount++;
    }
  }

  return removedCount;
}

/**
 * Reorder countdowns by updating sortOrder for multiple countdowns at once.
 *
 * @param orderedIds - Array of countdown IDs in the desired display order.
 * @param userId - The current user's Cognito sub ID.
 * @throws Error if any countdown ID is not found.
 */
export async function reorderCountdowns(
  orderedIds: string[],
  userId: string
): Promise<void> {
  const now = new Date().toISOString();

  await db.transaction('rw', db.countdowns, db.syncQueue, async () => {
    for (let i = 0; i < orderedIds.length; i++) {
      const countdownId = orderedIds[i]!;
      const existing = await db.countdowns.get(countdownId);

      if (!existing || existing.userId !== userId) {
        throw new Error(`Countdown not found: ${countdownId}`);
      }

      const updated: CountdownTimer = {
        ...existing,
        sortOrder: i,
        updatedAt: now,
        syncStatus: SyncStatus.Pending,
      };

      await db.countdowns.put(updated);

      await enqueueSyncChange(countdownId, 'update', { countdownId, sortOrder: i });
    }
  });
}
